import { useMemo, type FC } from 'react';
import type { ReceiptProps } from './type';

import styled from 'styled-components';

import {
  COLOR_BLUE_6,
  COLOR_LIGHT_GRAY,
  filterBackgroundColor,
  sanitizeRichText,
  shade,
  generateAccessKey
} from 'shared/helpers/index';

import { Wrapper } from '../Common';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import useSales from 'shared/hooks/useSales';

const Container = styled.div`
  padding: 80px 0;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 60px 0;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 40px 15px;
    `}
`;

const StyledWrapper = styled(Wrapper)<{ backgroundColor: string }>`
  background: ${(props) => props.backgroundColor};
  border: 1px solid ${COLOR_LIGHT_GRAY};
  padding: 34px 30px;
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
`;

const Order = styled.p`
  padding-bottom: 8px;
  border-bottom: 1px solid ${COLOR_LIGHT_GRAY};
`;

const Price = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 30px 0;
  border-bottom: 1px solid ${COLOR_LIGHT_GRAY};
`;

const Title = styled.p`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
`;

const PriceItem = styled.p`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};

  span {
    font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
    margin-right: 5px;
  }
`;

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 25px;
  align-items: center;
`;

const TitleTotal = styled.p`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
`;

const PriceTotal = styled.div`
  display: flex;
  align-items: center;
`;

const Currency = styled.p`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  margin-right: 14px;
  padding-top: 5px;
`;

const PriceItemTotal = styled.p`
  font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
`;

export const V1: FC<ReceiptProps> = (props) => {
  const {
    book_name,
    book_price,
    backgroundColor = COLOR_BLUE_6,
    currency,
    textColor,
    subscriptionData,
    order
  } = props;

  const { editable } = useWebsiteEditor();
  const { saleOrderDetails, upsellPrice, upsellDetails, isDashboard } = useSales();

  // Accessibility improvements
  const receiptAccessKey = useMemo(() => generateAccessKey('rcp', order), [order]);
  const itemAccessKey = useMemo(() => generateAccessKey('rci', order), [order]);
  const totalAccessKey = useMemo(() => generateAccessKey('rct', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-receipt-${order || 'receipt'}`, [order]);

  const dispayPrice = useMemo(() => {
    if (saleOrderDetails?.upsell_type === 'vip' && saleOrderDetails?.is_vip) {
      const vipPrice = Number(upsellPrice || 0);
      return `<p>${currency !== 'USD' ? `<span>${currency}</span>` : '$'} ${vipPrice}</p>`;
    }
    if (saleOrderDetails?.is_vip) {
      const vipPrice = Number(book_price || 0);
      return `<p>${currency !== 'USD' ? `<span>${currency}</span>` : '$'} ${vipPrice}</p>`;
    }
    if (editable && subscriptionData) {
      return `${subscriptionData.monthly_price}/m | ${subscriptionData.yearly_price}/y`;
    }
    if (subscriptionData) {
      return saleOrderDetails?.subscription_interval === 'month'
        ? `${subscriptionData.monthly_price}/m`
        : `${subscriptionData.yearly_price}/y`;
    }
    return `<p>${currency !== 'USD' ? `<span>${currency}</span>` : '$'} ${book_price}</p>`;
  }, [
    book_price,
    currency,
    editable,
    saleOrderDetails?.is_vip,
    saleOrderDetails?.subscription_interval,
    saleOrderDetails?.upsell_type,
    subscriptionData,
    upsellPrice
  ]);

  const upsellPriceValue = useMemo(() => {
    if (saleOrderDetails?.is_vip) {
      const vipPrice = Number(upsellPrice || 0);
      return `<p>${currency !== 'USD' ? `<span>${currency}</span>` : '$'} ${vipPrice}</p>`;
    }
    return '';
  }, [currency, saleOrderDetails?.is_vip, upsellPrice]);

  const displayTotalPrice = useMemo(() => {
    if (saleOrderDetails?.is_vip) {
      if (saleOrderDetails.upsell_type === 'bump') {
        const vipPrice = Number(book_price || 0) + Number(upsellPrice || 0);
        return `<p>${currency !== 'USD' ? `<span>${currency}</span>` : '$'} ${vipPrice}</p>`;
      }
      if (saleOrderDetails?.upsell_type === 'vip') {
        const vipPrice = Number(upsellPrice || 0);
        return `<p>${currency !== 'USD' ? `<span>${currency}</span>` : '$'} ${vipPrice}</p>`;
      }
    }
    if (editable && subscriptionData) {
      return `${subscriptionData.monthly_price}/m | ${subscriptionData.yearly_price}/y`;
    }
    if (subscriptionData) {
      return saleOrderDetails?.subscription_interval === 'month'
        ? `${subscriptionData.monthly_price}/m`
        : `${subscriptionData.yearly_price}/y`;
    }
    return book_price;
  }, [
    book_price,
    currency,
    editable,
    saleOrderDetails?.is_vip,
    saleOrderDetails?.subscription_interval,
    saleOrderDetails?.upsell_type,
    subscriptionData,
    upsellPrice
  ]);

  const shadedBackgroundColor = useMemo(() => {
    return shade(backgroundColor, 0.7);
  }, [backgroundColor]);

  return (
    <Container aria-label="Order receipt section" role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to order receipt</a>
      </span>

      <StyledWrapper
        backgroundColor={shadedBackgroundColor}
        id={skipLinkId}
        role="table"
        aria-label="Order receipt details"
        {...(!isDashboard && {
          accessKey: receiptAccessKey,
          title: `Press Alt+${receiptAccessKey} to focus the receipt`,
          tabIndex: 0
        })}>
        <Order
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText('<p>Order Receipt</p>')
          }}
          style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
          role="caption"
          aria-label="Receipt header"
        />

        <Price
          role="row"
          aria-label="Order item details"
          {...(!isDashboard && {
            accessKey: itemAccessKey,
            title: `Press Alt+${itemAccessKey} to focus order items`,
            tabIndex: 0
          })}>
          <Title
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(
                `<p>${saleOrderDetails?.upsell_type === 'vip' && saleOrderDetails.is_vip ? upsellDetails.product_name : book_name}</p>`
              )
            }}
            style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
            role="cell"
            aria-label="Product name"
          />
          <PriceItem
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(dispayPrice)
            }}
            style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
            role="cell"
            aria-label="Product price"
          />
        </Price>

        {saleOrderDetails?.is_vip && saleOrderDetails.upsell_type === 'bump' ? (
          <Price role="row" aria-label="Upsell item details">
            <Title
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(`<p>${upsellDetails?.product_name}</p>`)
              }}
              style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
              role="cell"
              aria-label="Upsell product name"
            />
            <PriceItem
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(upsellPriceValue)
              }}
              style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
              role="cell"
              aria-label="Upsell product price"
            />
          </Price>
        ) : null}

        <Total
          role="row"
          aria-label="Order total"
          {...(!isDashboard && {
            accessKey: totalAccessKey,
            title: `Press Alt+${totalAccessKey} to focus the total amount`,
            tabIndex: 0
          })}>
          <TitleTotal
            dangerouslySetInnerHTML={{ __html: sanitizeRichText('<p>Total</p>') }}
            style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
            role="cell"
            aria-label="Total label"
          />
          <PriceTotal role="cell" aria-label={`Total amount: ${currency} ${displayTotalPrice}`}>
            <Currency
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(currency)
              }}
              style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
              aria-label={`Currency: ${currency}`}
            />
            <PriceItemTotal
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(`${displayTotalPrice}`)
              }}
              style={{ color: filterBackgroundColor(shadedBackgroundColor, textColor) }}
              aria-label={`Total price: ${displayTotalPrice}`}
            />
          </PriceTotal>
        </Total>
      </StyledWrapper>
    </Container>
  );
};

V1.displayName = 'ReceiptV1';
